import DOMPurify from "dompurify";

const ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "b",
  "i",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "blockquote",
  "pre",
  "code",
  "a",
  "img",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  "div",
  "span",
  "figure",
  "figcaption",
];

const ALLOWED_ATTRIBUTES = {
  "a": ["href", "title", "target"],
  "img": ["src", "alt", "width", "height", "title"],
  "*": ["class"],
  "table": ["class", "colspan", "rowspan", "width", "height"],
  "thead": ["class", "colspan", "rowspan", "width", "height"],
  "tbody": ["class", "colspan", "rowspan", "width", "height"],
  "tr": ["class", "colspan", "rowspan", "width", "height"],
  "th": ["class", "colspan", "rowspan", "width", "height"],
  "td": ["class", "colspan", "rowspan", "width", "height"],
  "figure": ["class", "colspan", "rowspan", "width", "height"],
  "figcaption": ["class", "colspan", "rowspan", "width", "height"],
  "blockquote": ["class", "style"],
};

const SANITIZE_CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR: Object.keys(ALLOWED_ATTRIBUTES).reduce((acc, tag) => {
    if (tag === "*") {
      acc.push(...ALLOWED_ATTRIBUTES[tag]);
    }
    else {
      acc.push(...ALLOWED_ATTRIBUTES[tag as keyof typeof ALLOWED_ATTRIBUTES]);
    }
    return acc;
  }, [] as string[]),
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: ["script", "object", "embed", "form", "input", "button"],
  KEEP_CONTENT: true,
} as const;

export const sanitizeHtml = (html: string): string => {
  if (typeof window === "undefined") {
    return html;
  }
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: SANITIZE_CONFIG.ALLOWED_TAGS,
    ALLOWED_ATTR: SANITIZE_CONFIG.ALLOWED_ATTR,
    ALLOW_DATA_ATTR: SANITIZE_CONFIG.ALLOW_DATA_ATTR,
    FORBID_TAGS: [...SANITIZE_CONFIG.FORBID_TAGS],
    KEEP_CONTENT: SANITIZE_CONFIG.KEEP_CONTENT,
  });
};

const MOBILE_SAFE_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "u",
  "b",
  "i",
  "h2",
  "h3",
  "ul",
  "ol",
  "li",
  "a",
];

const MOBILE_SAFE_ALLOWED_ATTR = ["href", "title", "target", "rel"];

const MOBILE_SAFE_FORBID_TAGS = [
  "script",
  "object",
  "embed",
  "form",
  "input",
  "button",
  "img",
  "figure",
  "table",
  "span",
  "div",
  "h1",
];

/**
 * Sanitize HTML to a strict mobile-safe subset.
 * Strips images, font sizes, colors, alignment, tables and other tags that
 * cannot be reliably rendered by the yeati-benef mobile app.
 * Inline content of forbidden tags is preserved (KEEP_CONTENT).
 */
export const sanitizeMobileSafeHtml = (html: string): string => {
  if (!html) return "";
  if (typeof window === "undefined") return html;
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: MOBILE_SAFE_ALLOWED_TAGS,
    ALLOWED_ATTR: MOBILE_SAFE_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: MOBILE_SAFE_FORBID_TAGS,
    KEEP_CONTENT: true,
  });
};

const RICH_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "b",
  "i",
  "ul",
  "ol",
  "li",
  "span",
];

const RICH_ALLOWED_ATTR = ["class", "style"];

const RICH_FORBID_TAGS = [
  "script",
  "object",
  "embed",
  "form",
  "input",
  "button",
  "img",
];

/**
 * Sanitize CKEditor rich text for safe display. Keeps inline emphasis
 * (bold/italic/strikethrough), bullet/numbered lists and inline `style` on
 * `<span>` so font color, background color and size survive. Strips scripts,
 * event handlers and embedded objects. Narrower than `sanitizeHtml` on purpose
 * to avoid widening the inline-style XSS surface for the other consumers.
 */
export const sanitizeRichHtml = (html: string): string => {
  if (!html) return "";
  if (typeof window === "undefined") return html;
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: RICH_ALLOWED_TAGS,
    ALLOWED_ATTR: RICH_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: RICH_FORBID_TAGS,
    KEEP_CONTENT: true,
  });
};

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * Convert plain text (from a textarea) to minimal HTML that preserves
 * paragraph and line breaks for downstream HTML renderers (e.g. the
 * yeati-benef mobile app). Splits on blank lines into <p> blocks and
 * converts remaining single line breaks into <br>. Escapes HTML special
 * characters to prevent injection.
 */
export const multilineTextToHtml = (text: string): string => {
  const trimmed = text.trim();
  if (!trimmed) return "";
  return trimmed
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, "<br>")}</p>`)
    .join("");
};

/**
 * Convert stored HTML (produced by multilineTextToHtml or by the legacy
 * CKEditor) back to a multi-line plain string suitable for a textarea.
 * Preserves paragraph breaks as \n\n and <br> as \n.
 */
export const htmlToMultilineText = (html: string): string => {
  if (!html) return "";
  let normalized = html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<\/(?:div|h[1-6]|li)>/gi, "\n");

  if (typeof window !== "undefined") {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = normalized;
    normalized = tempDiv.textContent || tempDiv.innerText || "";
  }
  else {
    normalized = normalized.replace(/<[^>]*>/g, "");
  }

  return normalized.replace(/\n{3,}/g, "\n\n").trim();
};

export const extractTextFromHtml = (htmlContent: string): string => {
  if (typeof window === "undefined") {
    return htmlContent.replace(/<[^>]*>/g, "");
  }
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;
  return tempDiv.textContent || tempDiv.innerText || "";
};

export const truncateHtmlContent = (
  htmlContent: string,
  lines: number = 5,
): string => {
  const textContent = extractTextFromHtml(htmlContent);
  const words = textContent.split(" ");
  const wordsPerLine = 15;
  const maxWords = lines * wordsPerLine;

  if (words.length <= maxWords) {
    return htmlContent;
  }

  return words.slice(0, maxWords).join(" ") + "...";
};
